"use client";
import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/Dialog";
import MainForms from "@/app/[locale]/auth/login/components/LogistrationV1/MainForms";
import PasswordRecoveryForms from "@/app/[locale]/auth/login/components/LogistrationV1/PasswordRecoveryForms";
import { StartAsPage } from "@/app/[locale]/auth/login/components/types";
import { useLogistration } from "@/app/[locale]/auth/login/hooks/useLogistration";
import useLogistrationPageRedesign from "@/monarch/rules/logistrationPageRedesign/useLogistrationPageRedesign";
import Icon from "../icons";
import { useAuth } from "../AuthProvider";
import { TPALogistrationPage } from "@/lib/auth/getThirdPartyAuthProviders";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  startAs?: StartAsPage;
  updateLoggedInStatus: () => void;
}

export default function AuthModal({
  isOpen,
  onClose,
  startAs = StartAsPage.Login,
  updateLoggedInStatus,
}: AuthModalProps) {
  const { data } = useLogistrationPageRedesign();
  const { tpaProviders, fetchTpaProviders } = useAuth();

  const { loginInfo, registerInfo } = data || {};

  const {
    currentForm,
    isRegistration,
    isPasswordRecovery,
    isPasswordRecoveryComplete,
    switchToRegistration,
    switchToLogin,
    switchToLoginPasswordRecovery,
    switchToPasswordRecovery,
  } = useLogistration(startAs, true);

  const title = isPasswordRecovery
    ? "Reset Password"
    : isRegistration
      ? registerInfo?.formTitle
      : loginInfo?.formTitle;

  useEffect(() => {
    if (isOpen) {
      fetchTpaProviders(TPALogistrationPage.RegisterOrLogin, true);
    }
  }, [fetchTpaProviders, isOpen]);

  const handleClose = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="absolute w-screen h-screen overflow-y-auto left-0 top-0 translate-x-0 translate-y-0 rounded-none md:max-w-[680px] md:h-auto md:max-h-[98vh] md:left-[50%] md:top-[50%] md:translate-x-[-50%] md:translate-y-[-50%] md:rounded-[12px] bg-putty-200 [&>button[aria-label='Close']]:hidden p-0">
        {/* Mobile header */}
        <div className="sm:hidden sticky top-0 z-10 w-full bg-white border-b border-putty-400 p-4 grid grid-cols-3 items-center">
          <span />
          <Image
            height={45}
            width={87}
            src={"/trademark-logos/edx-logo-elm.svg"}
            alt={"edX Modal Logo"}
            title="edX"
            className="justify-self-center"
          />
          <DialogClose asChild>
            <button aria-label="Close" className="justify-self-end p-2 -mr-2">
              <Icon
                type="close-material"
                svgProps={{ width: 20, height: 20 }}
              />
            </button>
          </DialogClose>
        </div>
        {/* Desktop close button */}
        <div className="hidden md:block absolute top-4 right-4 z-20">
          <DialogClose asChild>
            <button
              aria-label="Close"
              className="bg-white rounded-full w-[40px] h-[40px] flex items-center justify-center border-2 border-putty-500"
            >
              <Icon
                type="close-material"
                svgProps={{ width: 24, height: 24 }}
                pathProps={{ fill: "#87857E" }}
              />
            </button>
          </DialogClose>
        </div>
        <div className="py-8 md:px-14">
          <DialogHeader>
            <DialogTitle className="text-3xl font-black italic text-primary-500 md:mb-4 ml-6 md:ml-0 text-left">
              {title}
            </DialogTitle>
          </DialogHeader>
          {isPasswordRecovery ? (
            <PasswordRecoveryForms
              currentPage={currentForm}
              switchToLogin={switchToLoginPasswordRecovery}
              isModal
            />
          ) : (
            <MainForms
              isRegistration={isRegistration}
              switchToRegistration={switchToRegistration}
              switchToLogin={switchToLogin}
              switchToPasswordRecovery={switchToPasswordRecovery}
              tpaProviders={tpaProviders ?? undefined}
              isPasswordRecoveryComplete={isPasswordRecoveryComplete}
              isModal
              onClose={onClose}
              updateLoggedInStatus={updateLoggedInStatus}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
